import { useState } from "react";
import {
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { AppButton, AppMenuItem } from "../../../components/form";

type ActionFn = () => void | Promise<void>;

type Props = {
  jobId: string | null;
  running: boolean;
  onValidate: ActionFn;
  onPreviewChain: ActionFn;
  onGenerateAnalysis: ActionFn;
  onOpenAnalysis: ActionFn;
  onPublish: ActionFn;
  onRun: ActionFn;
  onViewRuns: ActionFn;
  onDeleteJob: ActionFn;
};

export default function PrepDesignActionsMenu({
  jobId,
  running,
  onValidate,
  onPreviewChain,
  onGenerateAnalysis,
  onOpenAnalysis,
  onPublish,
  onRun,
  onViewRuns,
  onDeleteJob,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);

  const run = async (action: ActionFn) => {
    close();
    await action();
  };

  return (
    <>
      <AppButton
        variant="outlined"
        color="primary"
        busyIgnore
        endIcon={running ? <CircularProgress size={16} /> : <MoreVertIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
      >
        {t("prep.design.actionsMenu")}
      </AppButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: custom.components.dataPrep.designActionsMenuPaper } }}
      >
        <AppMenuItem onClick={() => run(onValidate)}>
          <ListItemIcon>
            <CheckCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.design.validate")}</ListItemText>
        </AppMenuItem>
        <AppMenuItem onClick={() => run(onPreviewChain)}>
          <ListItemIcon>
            <HubOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.design.previewChain")}</ListItemText>
        </AppMenuItem>
        <AppMenuItem onClick={() => run(onGenerateAnalysis)}>
          <ListItemIcon>
            <AutoAwesomeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.design.generateAnalysis")}</ListItemText>
        </AppMenuItem>
        <AppMenuItem onClick={() => run(onOpenAnalysis)}>
          <ListItemIcon>
            <ArticleOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.design.openAnalysis")}</ListItemText>
        </AppMenuItem>
        <Divider />
        <AppMenuItem onClick={() => run(onPublish)}>
          <ListItemIcon>
            <CloudUploadOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.design.publish")}</ListItemText>
        </AppMenuItem>
        <AppMenuItem onClick={() => run(onRun)} disabled={running}>
          <ListItemIcon>
            <PlayArrowOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.design.run")}</ListItemText>
        </AppMenuItem>
        {jobId && (
          <AppMenuItem onClick={() => run(onViewRuns)}>
            <ListItemIcon>
              <HistoryOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("prep.design.viewRuns")}</ListItemText>
          </AppMenuItem>
        )}
        {jobId && <Divider />}
        {jobId && (
          <AppMenuItem onClick={() => run(onDeleteJob)} sx={custom.pages.shared.errorMenuItem}>
            <ListItemIcon sx={custom.pages.shared.errorListItemIcon}>
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("prep.list.deleteJob")}</ListItemText>
          </AppMenuItem>
        )}
      </Menu>
    </>
  );
}
